/**
 * What a person is told when a write is refused because something already
 * exists, or because they have run out of something they were sold.
 *
 * Before this file, every one of these showed the user the single word
 * "Conflict." The reason was transmitted and then discarded: the API answers
 * `{"error":"conflict","message":"<the real code>"}`, the SPA read only
 * `error`, and "conflict" matched no sentence, so the last-resort formatter
 * turned the envelope code into a word. A customer who had used all 25 of the
 * data sources on their plan was told the system had a "Conflict."
 *
 * Two of these codes are PARAMETERISED and must be rendered rather than looked
 * up — a quota carries the limit the customer is actually on, and a database
 * duplicate carries the table and columns that collided. Throwing those away
 * at the edge is the same mistake one level down.
 *
 * `conflict-copy.test.ts` walks the API source and the migrations and fails if
 * any code this product can emit has no sentence here, so this table cannot
 * quietly fall behind the API.
 */

/**
 * The last resort, and the guard test exists so that no code the API can
 * actually emit ever reaches it. It is still a real sentence: an unmapped code
 * is a gap in this table, and the person in front of it should be told
 * something true and actionable rather than shown our envelope.
 */
export const GENERIC_CONFLICT =
  'That clashes with something that already exists. Reload the page and try again.';

/**
 * Refusals the API writes by hand, as `conflict('<code>')`.
 */
export const CONFLICT_MESSAGES: Record<string, string> = {
  // Names and addresses somebody else holds
  slug_in_use: 'That workspace URL is already taken. Please choose a different one.',
  org_slug_in_use: 'That organisation URL is already taken. Please choose a different one.',
  hostname_in_use: 'That domain is already in use by another workspace.',

  // Sharing a brand's assets with a partner agency
  grant_already_live: 'That asset is already shared with this partner.',
  grant_already_revoked: 'That share has already been withdrawn.',

  // Asking for temporary access to personal data
  elevation_already_live:
    'You already have approved access to personal data — there is no need to ask again.',

  // Moving a workspace between organisations
  to_org_not_active:
    "That organisation isn't active, so a workspace can't be transferred to it yet.",
  transfer_already_pending: 'A transfer of this workspace is already waiting for review.',
  transfer_payer_unresolved:
    'Nobody is set to pay for this workspace after the move. Choose who pays, then try again.',
  transfer_not_pending: 'This transfer has already been decided.',
  brand_owner_changed:
    'This workspace changed hands while the transfer was waiting, so it can no longer go ahead. Please start a new one.',

  // A workspace has to keep at least one owner, or nobody can administer it and
  // there is no way back: promoting somebody is itself an owner/admin act.
  sole_owner_cannot_be_demoted:
    'This is the only owner of the workspace. Make somebody else an owner first, then lower this person.',
  sole_owner_cannot_be_removed:
    'This is the only owner of the workspace. Make somebody else an owner first, then remove this person.',

  // Inviting somebody who is already a member, at a LOWER role than they hold.
  // Reached in the bulk report, where a batch is pasted at one role and nobody
  // has looked at any individual row. The single-invite form asks instead, and
  // retries with the confirmation, so it never shows this.
  role_change_requires_confirmation:
    'They are already a member at a higher role. Inviting them at a lower one would take access away, so it has to be confirmed one at a time.',

  // A data source that exists but cannot be read as saved. Was a 500 until
  // 2026-09-06, which put an admin's one-field mistake in the error budget.
  leads_source_missing_gid:
    'The leads source for this region is missing the tab id (gid) it needs. An admin can add it in Admin \u2192 Data sources.',

  // Seats, enforced at the organisation rather than per brand
  org_seat_cap_exceeded:
    'Every seat on your plan is taken. Remove a member, or upgrade your plan in Billing to add more.',
};

/**
 * `quota_exceeded:<key>:<used>/<limit>` — one sentence per quota, because
 * "you have hit a limit" is not something anybody can act on. Each names the
 * thing that ran out, the number they are on, and the two ways forward.
 */
const QUOTA_MESSAGES: Record<string, (limit: number) => string> = {
  data_sources: (n) =>
    `You've connected all ${n} data sources your plan includes. Disconnect one you no longer need, or upgrade your plan in Billing to add more.`,
  dashboards: (n) =>
    `You've built all ${n} dashboards your plan includes. Delete one you no longer need, or upgrade your plan in Billing to add more.`,
  scheduled_reports: (n) =>
    `You've set up all ${n} scheduled reports your plan includes. Turn one off, or upgrade your plan in Billing to add more.`,
  embed_tokens: (n) =>
    `You've issued all ${n} embed links your plan includes. Revoke one you no longer need, or upgrade your plan in Billing to add more.`,
  seats: (n) =>
    `All ${n} seats on your plan are taken. Remove a member, or upgrade your plan in Billing to add more.`,
};

const QUOTA_UNKNOWN =
  "You've reached a limit on your plan. Upgrade your plan in Billing to add more.";

/**
 * `duplicate.<table>.<columns>` — derived by the API from what SQLite said,
 * so the key is the constraint, not a hand-picked name. Mapping them here
 * rather than sending sentences from the API keeps the API's codes machine
 * shaped and derived, which is what lets a constraint added next year be
 * reported honestly by a route nobody edited.
 *
 * Several of these are plumbing rather than anything a person typed — a
 * session token, a magic-link token, a sync window. They share a "try again"
 * sentence ON PURPOSE, not by falling through: a customer should never be
 * shown the name of a table.
 */
const RETRY = 'Something went wrong on our end. Please try again.';

export const DUPLICATE_MESSAGES: Record<string, string> = {
  // Things a person fills in
  'duplicate.tenants.slug': 'That workspace URL is already taken. Please choose a different one.',
  'duplicate.brands.owner_org_id+slug':
    'Your organisation already has a workspace with that URL. Please choose a different one.',
  'duplicate.organizations.slug':
    'That organisation URL is already taken. Please choose a different one.',
  'duplicate.tenant_domains.hostname': 'That domain is already in use by another workspace.',
  'duplicate.dashboard_definitions.tenant_id+slug':
    'This workspace already has a dashboard with that URL. Please choose a different one.',
  'duplicate.users.email': 'That email is already registered.',
  'duplicate.tenant_users.tenant_id+user_id': 'That person is already a member of this workspace.',
  'duplicate.org_members.org_id+user_id': 'That person is already a member of this organisation.',
  'duplicate.platform_staff.user_id': 'That person already has Adholics staff access.',
  'duplicate.tenant_aliases.tenant_id+kind+alias':
    'That alias is already in use in this workspace.',
  'duplicate.oauth_connections.tenant_id+provider+account_id':
    'That advertising account is already connected to this workspace.',
  'duplicate.index.asset_grants_live_uidx': 'That asset is already shared with this partner.',

  // Two people saved the same versioned thing at once. Not the user's fault
  // and not worth explaining: reloading picks up the version that won.
  'duplicate.dashboard_versions.definition_id+version':
    'Someone else saved this dashboard a moment ago. Reload the page and try your change again.',
  'duplicate.naming_convention_versions.brand_id+version':
    'Someone else saved this naming convention a moment ago. Reload the page and try your change again.',
  'duplicate.naming_convention_versions.tenant_id+version':
    'Someone else saved this naming convention a moment ago. Reload the page and try your change again.',

  // One-per-workspace records that already exist
  'duplicate.naming_conventions.brand_id': 'This workspace already has a naming convention.',
  'duplicate.naming_conventions.tenant_id': 'This workspace already has a naming convention.',
  'duplicate.contracts.brand_id': 'This workspace already has a contract.',
  'duplicate.subscriptions.brand_id': 'This workspace already has a subscription.',
  'duplicate.legacy_tenant_map.tenant_id': 'This workspace has already been set up.',
  'duplicate.dunning_runs.brand_id':
    'This workspace already has an overdue-payment process running.',

  // Billing plumbing the customer did not initiate
  'duplicate.subscriptions.stripe_subscription_id':
    'That Stripe subscription is already linked to a workspace.',
  'duplicate.billing_events.stripe_event_id': "We've already recorded that billing event.",
  'duplicate.dunning_deletions.run_id':
    'A deletion request has already been raised for this account.',

  // Data we ingest on the customer's behalf
  'duplicate.lead_facts.lead_id': 'That lead has already been uploaded.',
  'duplicate.lead_webhooks.brand_id': 'This workspace already has a lead webhook.',
  'duplicate.sync_jobs.data_source_id+window_key':
    'That data source has already been synced for this period.',

  // Pure plumbing. Never name the table.
  'duplicate.sessions.refresh_token_hash': RETRY,
  'duplicate.magic_link_tokens.token_hash':
    'Something went wrong sending your sign-in link. Please request a new one.',
};

/**
 * The sentence for a 409 code, or null if this is not one of ours.
 *
 * `details` is consulted for exactly two codes. The API already composes a
 * better sentence for a taken workspace name than anything that can be written
 * here — it knows WHO holds the name, and whether that workspace is archived
 * and should be restored rather than replaced. Only those two codes read it:
 * other endpoints put operator instructions in `hint` ("set the secret via
 * wrangler"), which must never reach a customer.
 */
const HINTED_CODES = new Set(['slug_in_use', 'org_slug_in_use']);

export function conflictMessage(
  code: string,
  details?: Record<string, unknown> | null,
): string | null {
  if (HINTED_CODES.has(code)) {
    const hint = details?.hint;
    if (typeof hint === 'string' && hint.trim().length > 0) return hint;
  }
  const flat = CONFLICT_MESSAGES[code];
  if (flat) return flat;

  const dup = DUPLICATE_MESSAGES[code];
  if (dup) return dup;

  if (code.startsWith('quota_exceeded')) return quotaMessage(code);

  return null;
}

function quotaMessage(code: string): string {
  const [, key, counts] = code.split(':');
  const render = key ? QUOTA_MESSAGES[key] : undefined;
  if (!render) return QUOTA_UNKNOWN;
  const limit = Number(counts?.split('/')[1]);
  if (!Number.isFinite(limit) || limit < 0) return QUOTA_UNKNOWN;
  return render(limit);
}
